<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn small icon @click="close">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h3 class="mx-2">Calculator Items</h3>
        </v-card-title>
        <v-card-text>
            <v-form ref="form" v-model="valid">
                <v-data-table
                    :headers="
                        isHPLModel || isAluminumModel
                            ? headersHPLModel
                            : headers
                    "
                    :items="items"
                    :loading="loading"
                    hide-default-footer
                    disable-pagination
                    :mobile-breakpoint="0"
                    class="elevation-0"
                    fixed-header
                >
                    <!--ITEMS-->
                    <template v-slot:[`item.model`]="{ item }">
                        <p class="my-0">
                            {{ item.model || model }}
                        </p>
                    </template>
                    <template v-slot:[`item.netEachPrice`]="{ item }">
                        <p class="my-0">
                            {{ customRound(getDisplayPrice(item)) }}
                        </p>
                    </template>
                    <template v-slot:[`item.quantity`]="{ item }">
                        <p class="my-0">
                            {{
                                invoice.status == 'billed'
                                    ? item.availableQuantity
                                    : item.quantity
                            }}
                        </p>
                    </template>
                    <template v-slot:[`item.selectQty`]="{ item }">
                        <v-text-field
                            v-model="item.selectQty"
                            type="number"
                            :rules="[validateQuantity(item)]"
                            style="width:60px;"
                            @blur="normalizeSelectQtyField(item)"
                        />
                    </template>
                    <template v-slot:[`item.currentPrice`]="{ item }">
                        <p class="my-0">
                            {{
                                customRound(
                                    getDisplayPrice(item) *
                                        (item.selectQty || 0)
                                )
                            }}
                        </p>
                    </template>
                </v-data-table>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                color="primary"
                class="mb-4"
                @click="selectItems"
                rounded
                :loading="loading"
                :disabled="loading || !canImport"
            >
                IMPORT
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'
import _ from 'lodash'
import * as invMoney from '@/utils/invoiceLineMoney.js'
import * as invoiceSavePrep from '@/utils/invoiceSavePrep.js'
import { costKind } from '@/utils/costKind.js'

export default {
    name: 'CalculatorItems',
    props: {
        quoteId: {
            type: String,
            required: true,
        },
        invoice: {
            type: Object,
            required: true,
        },
        model: {
            type: String,
            required: true,
        },
        project: {
            type: Object,
            default: null,
        },
    },
    data: () => ({
        HPL_MODEL: 'HPL PANELS',
        ALUMINUM_MODEL: 'ALUMINUM CLADDING',
        isHPLModel: false,
        isAluminumModel: false,
        valid: false,
        loading: false,
        items: [],
        headers: [
            { text: 'LINE ITEM NAME', value: 'lineItemName', sortable: false },
            { text: 'MODEL', value: 'model', sortable: false },
            { text: 'QUANTITY', value: 'quantity', sortable: false },
            { text: 'WIDTH (IN)', value: 'width', sortable: false },
            { text: 'HEIGHT (IN)', value: 'height', sortable: false },
            {
                text: 'BLANK OFF PANEL',
                value: 'blankOffPanel',
                sortable: false,
            },
            { text: 'BACK PANEL', value: 'backPan', sortable: false },
            { text: 'SILL PANEL', value: 'sillPan', sortable: false },
            { text: 'BIRD SCREEN', value: 'birdScreen', sortable: false },
            {
                text: 'NET PRICE / UNIT',
                value: 'netEachPrice',
                sortable: false,
            },
            { text: 'SELECT QTY', value: 'selectQty', sortable: false },
            {
                text: 'CURRENT PRICE',
                value: 'currentPrice',
                sortable: false,
                align: 'center',
            },
        ],
        headersHPLModel: [
            { text: 'LINE ITEM NAME', value: 'lineItemName', sortable: false },
            { text: 'MODEL', value: 'model', sortable: false },
            { text: 'SPECIFICATION', value: 'specification', sortable: false },
            { text: 'QUANTITY', value: 'quantity', sortable: false },
            { text: 'AREA', value: 'areaString', sortable: false },
            { text: 'ORIENTATION', value: 'orientation', sortable: false },
            { text: 'MODULATION', value: 'modulation', sortable: false },
            // { text: 'FINISH', value: 'finish', sortable: false },
            {
                text: 'NET PRICE / UNIT',
                value: 'netEachPrice',
                sortable: false,
            },
            { text: 'SELECT QTY', value: 'selectQty', sortable: false },
            {
                text: 'CURRENT PRICE',
                value: 'currentPrice',
                sortable: false,
                align: 'center',
            },
        ],
    }),
    computed: {
        canImport() {
            if (!this.items.length) {
                return false
            }
            const hasPositiveSelection = this.items.some(
                item => this.normalizeSelectQty(item.selectQty) > 0
            )
            if (!hasPositiveSelection) {
                return false
            }
            return this.items.every(item => this.isSelectQtyValid(item))
        },
    },
    async mounted() {
        try {
            this.loading = true
            // validate model
            if (this.model == this.HPL_MODEL) {
                this.isHPLModel = true
            } else if (this.model == this.ALUMINUM_MODEL) {
                this.isAluminumModel = true
            }
            if (
                this.invoice.itemsFromCalculator == undefined ||
                this.invoice.itemsFromCalculator[this.quoteId] == undefined
            ) {
                this.items = await API.getCalculator(this.quoteId)
                this.items = this.items.map(item => {
                    delete item.totalPrice
                    return item
                })
            } else {
                this.items = _.cloneDeep(
                    this.invoice.itemsFromCalculator[this.quoteId]
                )
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        normalizeSelectQty(value) {
            if (value === '' || value === null || value === undefined) {
                return 0
            }
            const qty = Number(value)
            return Number.isNaN(qty) ? 0 : qty
        },
        normalizeSelectQtyField(item) {
            item.selectQty = this.normalizeSelectQty(item.selectQty)
        },
        getMaxSelectableQty(item) {
            if (this.invoice.status == 'billed') {
                return Number(item.availableQuantity ?? 0)
            }
            return Math.max(0, Number(item.quantity ?? 0))
        },
        isSelectQtyValid(item) {
            const qty = this.normalizeSelectQty(item.selectQty)
            if (qty < 0) {
                return false
            }
            return qty <= this.getMaxSelectableQty(item)
        },
        selectItems() {
            if (!this.canImport) {
                return
            }
            this.items.forEach(item => this.normalizeSelectQtyField(item))
            if (this.isHPLModel) {
                this.importItemsHPLModel()
            } else if (this.isAluminumModel) {
                this.importItemsAluminum()
            } else {
                this.importItems()
            }
        },
        close() {
            this.$emit('close')
        },
        customRound(value) {
            return new Intl.NumberFormat('de-DE', {
                maximumFractionDigits: 0,
            }).format(value % 1 > 0.5 ? Math.ceil(value) : Math.floor(value))
        },
        getDisplayPrice(item) {
            return item.fixedNetUnitPrice || item.netEachPrice
        },
        getTotalPrice(item) {
            const price = item.fixedNetUnitPrice || item.netEachPrice
            if (price && item.quantity) {
                return price * item.quantity
            }
            return item.totalPrice || 0
        },
        importItemsHPLModel() {
            // filter out items with selectQty > 0
            const newItems = this.items.filter(item => {
                item.selectQty = Number(item.selectQty || 0)
                return item.selectQty > 0
            })
            if (newItems.length === 0) {
                return null
            }
            // set items to invoice
            if (this.invoice.itemsFromCalculator == undefined) {
                this.invoice.itemsFromCalculator = {
                    [this.quoteId]: this.items.map(item => {
                        delete item.createdOn
                        delete item.updatedOn
                        return item
                    }),
                }
            } else {
                this.invoice.itemsFromCalculator[this.quoteId] = this.items.map(
                    item => {
                        delete item.createdOn
                        delete item.updatedOn
                        return item
                    }
                )
            }
            // set quantity invoice
            for (const cost of this.invoice.items[this.quoteId]) {
                let invoiceQuantity = 0
                const selectedItems = newItems.filter(item => {
                    const costName = cost.name.replace(/\s+/g, '')
                    const specification = item.specification.replace(/\s+/g, '')
                    return costName
                        .toLowerCase()
                        .includes(specification.toLowerCase())
                })
                if (selectedItems.length > 0) {
                    invoiceQuantity = selectedItems.reduce(
                        (accum, item) => accum + item.selectQty,
                        0
                    )
                } else {
                    continue
                }
                this.applyImportedQuantity(cost, invoiceQuantity)
            }
            this.$emit('replaceInvoice', this.invoice)
        },
        importItemsAluminum() {
            // filter out items with selectQty > 0
            const newItems = this.items.filter(item => {
                item.selectQty = Number(item.selectQty || 0)
                return item.selectQty > 0
            })
            if (newItems.length === 0) {
                return null
            }
            // set items to invoice
            if (this.invoice.itemsFromCalculator == undefined) {
                this.invoice.itemsFromCalculator = {
                    [this.quoteId]: this.items.map(item => {
                        delete item.createdOn
                        delete item.updatedOn
                        return item
                    }),
                }
            } else {
                this.invoice.itemsFromCalculator[this.quoteId] = this.items.map(
                    item => {
                        delete item.createdOn
                        delete item.updatedOn
                        return item
                    }
                )
            }
            // set quantity invoice
            for (const cost of this.invoice.items[this.quoteId]) {
                let invoiceQuantity = 0
                // Mapear items a costs usando la misma lógica que HPL: comparar cost.name (descripción presentable) con item.specification
                const selectedItems = newItems.filter(item => {
                    if (!item.specification) return false
                    const costName = cost.name.replace(/\s+/g, '').toLowerCase()
                    const specification = item.specification
                        .replace(/\s+/g, '')
                        .toLowerCase()
                    return (
                        costName.includes(specification) ||
                        specification.includes(costName)
                    )
                })

                if (selectedItems.length > 0) {
                    invoiceQuantity = selectedItems.reduce(
                        (accum, item) => accum + item.selectQty,
                        0
                    )
                } else {
                    continue
                }

                this.applyImportedQuantity(cost, invoiceQuantity)
            }
            this.$emit('replaceInvoice', this.invoice)
        },
        importItems() {
            // filter out items with selectQty > 0
            const newItems = this.items.filter(
                item => Number(item.selectQty || 0) > 0
            )
            if (newItems.length === 0) {
                return null
            }
            // set items to invoice
            if (this.invoice.itemsFromCalculator == undefined) {
                this.invoice.itemsFromCalculator = {
                    [this.quoteId]: this.items.map(item => {
                        delete item.createdOn
                        delete item.updatedOn
                        return item
                    }),
                }
            } else {
                this.invoice.itemsFromCalculator[this.quoteId] = this.items.map(
                    item => {
                        delete item.createdOn
                        delete item.updatedOn
                        return item
                    }
                )
            }
            // filter by model
            // filter by accessories
            const blankOffPanel = newItems.filter(item => item.blankOffPanel)
            const backPan = newItems.filter(
                item => item.backPan && !item.blankOffPanel
            )
            const sillPan = newItems.filter(item => item.sillPan)
            const birdScreen = newItems.filter(item => item.birdScreen)
            // set quantity invoice
            for (const cost of this.invoice.items[this.quoteId]) {
                let invoiceQuantity = 0
                const kind = costKind(cost)
                if (kind === 'impactLouver') {
                    const totalArea =
                        newItems.reduce(
                            (acc, item) =>
                                acc +
                                Number(item.selectQty) *
                                    item.width *
                                    item.height *
                                    0.00064516,
                            0
                        ) * 10.7639
                    invoiceQuantity =
                        totalArea > 0
                            ? invMoney.roundLineQty(Number(totalArea.toFixed(2)))
                            : 0
                } else if (kind === 'blankOffPanel') {
                    const totalArea =
                        blankOffPanel.reduce(
                            (acc, item) =>
                                acc +
                                Number(item.selectQty) *
                                    item.width *
                                    item.height *
                                    0.00064516,
                            0
                        ) * 10.7639
                    invoiceQuantity =
                        totalArea > 0 ? invMoney.roundLineQty(totalArea) : 0
                } else if (kind === 'backPan') {
                    const totalArea =
                        backPan.reduce(
                            (acc, item) =>
                                acc +
                                Number(item.selectQty) *
                                    item.width *
                                    item.height *
                                    0.00064516,
                            0
                        ) * 10.7639
                    invoiceQuantity =
                        totalArea > 0 ? invMoney.roundLineQty(totalArea) : 0
                } else if (kind === 'sillPan') {
                    const totalArea = sillPan.reduce(
                        (acc, item) =>
                            acc + Number(item.selectQty) * item.vainLength,
                        0
                    )
                    invoiceQuantity =
                        totalArea > 0
                            ? invMoney.roundLineQty(totalArea * 3.28084)
                            : 0
                } else if (kind === 'birdScreen') {
                    const totalArea =
                        birdScreen.reduce(
                            (acc, item) =>
                                acc +
                                Number(item.selectQty) *
                                    item.width *
                                    item.height *
                                    0.00064516,
                            0
                        ) * 10.7639
                    invoiceQuantity =
                        totalArea > 0 ? invMoney.roundLineQty(totalArea) : 0
                } else if (kind === 'freight') {
                    const totalArea =
                        newItems.reduce(
                            (acc, item) =>
                                acc +
                                Number(item.selectQty) *
                                    item.width *
                                    item.height *
                                    0.00064516,
                            0
                        ) * 10.7639
                    invoiceQuantity =
                        totalArea > 0 ? invMoney.roundLineQty(totalArea) : 0
                } else {
                    continue
                }
                this.applyImportedQuantity(cost, invoiceQuantity)
            }
            this.$emit('replaceInvoice', this.invoice)
        },
        validateQuantity(item) {
            return value => {
                const qty = this.normalizeSelectQty(value)
                if (qty < 0) {
                    return 'Quantity must be greater than or equal to 0'
                }
                const maxQty = this.getMaxSelectableQty(item)
                if (qty > maxQty) {
                    return maxQty > 0
                        ? `Quantity must be at most ${maxQty}`
                        : 'No quantity remaining for this item'
                }
                return true
            }
        },
        availableItemQty(item) {
            let qty = 0
            const logs = item.logs || []
            logs.forEach(invoice => {
                qty +=
                    (invoice.billedQty || invoice.invoiceQuantity || 0) -
                    (invoice.creditQty || 0)
            })
            return item.qty - qty
        },
        availableCreditQty(item) {
            return (
                (item.billedQty || item.creditNoteQty) - (item.creditQty || 0)
            )
        },
        billedItemQty(item) {
            let qty = 0
            const logs = item.logs || []
            logs.forEach(invoice => {
                qty +=
                    (invoice.billedQty || invoice.invoiceQuantity || 0) -
                    (invoice.creditQty || 0)
            })
            return qty
        },
        roundToDecimals(value, decimals = 2) {
            if (value == null || Number.isNaN(value)) return 0
            const factor = 10 ** decimals
            return Math.round(Number(value) * factor) / factor
        },
        calculateBilledQty(item) {
            return invoiceSavePrep.calculateBilledQty(item, this.project)
        },
        availableToBillQty(item) {
            const qty = Number(item.qty || 0)
            const billedQty = Number(this.calculateBilledQty(item) || 0)
            const price = Number(item.pricePerUnit || 0)
            let maxQty = 0
            if (qty - billedQty > 0) {
                maxQty = Math.max(0, this.availableItemQty(item))
            }
            if (price !== 0) {
                const balance = this.roundToDecimals(
                    qty * price - billedQty * price,
                    3
                )
                if (balance !== 0) {
                    const fromBalance = this.roundToDecimals(
                        balance / price,
                        4
                    )
                    if (maxQty <= 0) {
                        maxQty = fromBalance
                    } else {
                        maxQty = Math.min(maxQty, fromBalance)
                    }
                }
            }
            return this.roundToDecimals(maxQty, 4)
        },
        lineBillableMinorRangeForCost(item) {
            const balM = invMoney.lineSignedBalanceMinor(
                Number(item.qty || 0),
                Number(this.calculateBilledQty(item) || 0),
                Number(item.pricePerUnit || 0)
            )
            const range = invMoney.billableAmountMinorRange(balM)
            const maxQty = this.availableToBillQty(item)
            return invMoney.intersectBillableMinorRange(
                range.minM,
                range.maxM,
                maxQty,
                Number(item.pricePerUnit || 0)
            )
        },
        /** Align qty/currentInvoice with InvoiceRequest (clamp to billable balance). */
        syncCostInvoiceAmount(cost) {
            const price = Number(cost.pricePerUnit || 0)
            if (!price) {
                cost.invoiceQuantity = 0
                cost.currentInvoice = 0
                return
            }
            const maxQty = this.availableToBillQty(cost)
            let invQty = invMoney.roundLineQty(
                Math.min(Number(cost.invoiceQuantity || 0), maxQty)
            )
            if (!invQty) {
                cost.invoiceQuantity = 0
                cost.currentInvoice = 0
                return
            }
            const desiredM = invMoney.toMinor(
                invMoney.roundLineAmount(invQty * price)
            )
            const { minM, maxM } = this.lineBillableMinorRangeForCost(cost)
            if (minM === 0 && maxM === 0 && desiredM === 0) {
                cost.invoiceQuantity = 0
                cost.currentInvoice = 0
                return
            }
            const clampedM = invMoney.clampMinorToRange(desiredM, minM, maxM)
            invMoney.applySignedLineAmountToItem(cost, clampedM, price)
            invMoney.normalizeInvoiceLineForApi(
                cost,
                Number(this.calculateBilledQty(cost) || 0)
            )
        },
        applyImportedQuantity(cost, invoiceQuantity) {
            const roundedQty = invMoney.roundLineQty(invoiceQuantity)
            if (
                this.invoice.status == 'billed' ||
                this.invoice.status == 'credit'
            ) {
                cost.creditQtyRequested = invMoney.roundLineQty(
                    Math.min(roundedQty, this.availableCreditQty(cost))
                )
                return
            }
            cost.invoiceQuantity = invMoney.roundLineQty(
                Math.min(roundedQty, this.availableToBillQty(cost))
            )
            this.syncCostInvoiceAmount(cost)
        },
    },
}
</script>
